import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { FeedbackFlags } from '../instructor/course-builder-dynamic/types/module-creation.types';

/**
 * @implements REQ-GRADE-203 - Question result with grading metadata
 */
export interface QuestionResult {
  questionIndex: number;
  questionText: string;
  questionType: string;
  userAnswer: any;
  points: number;
  score?: number;
  maxScore?: number;
  status?: string;
  isCorrect?: boolean | null;
  correctAnswer?: any;
  correctRows?: number[]; // For grid questions - array of row indices that were answered correctly
  correctCount?: number; // For select-all with partial credit
  incorrectCount?: number;
  totalCorrect?: number;
  /** KAN-254: instructor-authored explanation channels, attached by the
   *  backend only at the indicators_answers_explanations tier. */
  feedback?: { general?: string; correct?: string; incorrect?: string } | null;
}

/**
 * A single sanitized question from an attempt snapshot (KAN-348).
 * Options/rows are already in display order (KAN-350 shuffle applied at draw
 * time); answers are stripped for learners.
 */
export interface QuizAttemptQuestion {
  slotIndex?: number;
  type: string;
  text: string;
  options?: any[];
  gridRows?: any[];
  gridColumns?: any[];
  points?: number;
  [key: string]: any;
}

/**
 * A learner quiz attempt (KAN-348/349): the frozen drawn question set plus
 * resume/expiry metadata.
 */
export interface QuizAttempt {
  attemptId: string;
  quizId: string;
  status?: string;
  resumed: boolean;
  /** KAN-678: this resume re-froze the attempt because the quiz changed after the learner opened it. */
  refreshed?: boolean;
  questions: QuizAttemptQuestion[];
  passingScore?: number;
  startedAt?: string;
  savedAnswers?: any[];
  expiresAt?: string;
  timeLimit?: number;
  showFeedback?: boolean;
  feedbackTiming?: string;
  title?: string;
}

/** Optional context passed when starting an attempt. */
export interface AttemptContext {
  moduleId?: string;
  unitId?: string;
  courseId?: string;
}

/** Options for submitting a quiz (KAN-348). */
export interface SubmitOptions extends AttemptContext {
  attemptId?: string;
  timeSpent?: number;
  /** KAN-325 video gate: progress snapshot written before the completion check. */
  videoProgress?: { furthestPosition: number; duration: number };
}

/**
 * @implements REQ-GRADE-203 - View quiz submission answers with grading metadata
 */
export interface QuizSubmission {
  submissionId: string;
  quizId: string;
  userId: string;
  answers: any[];
  score: number;
  earnedPoints?: number;
  totalPoints?: number;
  /**
   * KAN-127 Phase 6: canonical 1-dp percentage — the SAME value the scoring
   * engine used for the pass/fail decision (computeQuizResult). The backend
   * already returns this on the /submissions/latest payload; surfacing it on the
   * interface lets the quiz-panel banner clamp render the true near-boundary
   * value (e.g. 79.5%) instead of the integer `score` (80) which would contradict
   * the Failed badge. Optional: absent on old cached payloads (banner falls back).
   */
  percentage?: number;
  passed: boolean;
  submittedAt: string;
  timeTaken?: number;
  moduleId?: string;
  unitId?: string;
  // KAN-334: TRUE when this is a synthetic instructor-granted submission
  // ("Credited by instructor (no attempt)"). The learner did not submit an attempt.
  isManualGrant?: boolean;
  /** KAN-627: the grant is prior completion credit -- shown as credit, not a score. */
  isPriorCredit?: boolean;
  questionResults?: QuestionResult[]; // REQ-GRADE-203: Grading metadata from backend
  /**
   * KAN-348 Δ5: the attempted (drawn/sanitized) questions when this submission
   * has an attempt snapshot — review renders exactly what the learner saw
   * (bank draws / shuffled order), not the current authored questions.
   */
  questions?: QuizAttemptQuestion[];
  gradingStatus?: 'auto_graded' | 'partial' | 'pending_manual';
  manualGradingPending?: number;
  manualPendingPoints?: number;
  bestPossiblePercentage?: number;
  guaranteedFail?: boolean;
  overriddenScore?: number | null;
  feedbackVisibility?: string;
  /**
   * KAN-343: the three authoritative feedback flags from the (clamped) submission
   * response. The quiz-panel gates on these; feedbackVisibility is the legacy
   * fallback. Absent on old cached payloads (panel falls back to the scalar).
   */
  feedbackFlags?: FeedbackFlags;
  /**
   * KAN-310: custom incorrect ("Note from instructor") message, gated by the
   * backend (failure + non-score_only) exactly like the live-submit response.
   * Present on the restore payload so the learner player can re-render the
   * failure banner on "Resume Course". Absent on old cached payloads.
   */
  wrongAnswerFeedback?: string | null;
  /**
   * KAN-327: the gated custom PASS ("success") message. Present on the submit +
   * restore payloads (gated to a PASS + non-score_only). Mirror of the above.
   */
  passFeedback?: string | null;
  /**
   * KAN-314 VS1: attempt accounting. Null when the quiz is unlimited/unenforced
   * (legacy). When `attemptsRemaining` is 0 the learner player withholds the
   * "Try Again" action and shows "No attempts remaining".
   */
  attemptLimit?: number | null;
  attemptsUsed?: number | null;
  attemptsRemaining?: number | null;
  /**
   * KAN-478: the learner holds a valid (non-invalidated) PASSING submission for
   * this quiz, independent of whether THIS submission passed. Post-pass retakes
   * are reachable, so the restored payload is often a lower-scoring retake whose
   * own `passed` would wrongly read as "never passed" — which withholds forward
   * navigation and fires KAN-388's terminal verdict on a completed course.
   * Absent on payloads from a backend that predates the field.
   */
  hasPassedAttempt?: boolean;
}

export interface DebugQuizAnswer {
  questionIndex: number;
  questionText: string;
  questionType: string;
  correctAnswer: any;
  options: { text: string; isCorrect: boolean }[];
  points: number;
}

export interface DebugQuizAnswers {
  quizId: string;
  answers: DebugQuizAnswer[];
}

@Injectable({
  providedIn: 'root'
})
export class QuizService {
  private apiUrl = `${environment.apiUrl}/quizzes`;

  constructor(private http: HttpClient) {}

  getUserQuizzes(userId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/user/${userId}`);
  }

  deleteQuiz(quizId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${quizId}`);
  }

  /**
   * KAN-348: Start (or resume) a learner attempt. The backend draws the
   * question set once (bank slots resolved, KAN-350 shuffle applied, variants
   * resolved — supersedes the old start-session flow) and freezes it; reloads
   * return the same open attempt (resumed: true) with any autosaved answers.
   */
  startAttempt(quizId: string, ctx: AttemptContext = {}): Observable<QuizAttempt> {
    return this.http
      .post<{ success: boolean; data: QuizAttempt }>(`${this.apiUrl}/${quizId}/attempts`, {
        moduleId: ctx.moduleId,
        unitId: ctx.unitId,
        courseId: ctx.courseId
      })
      .pipe(map(r => r.data));
  }

  /**
   * KAN-414: Instructor preview draw — a stateless sample resolution (no attempt
   * row, no draw history, fresh random pick per call) so preview surfaces can
   * render a bank-draw quiz's real questions. A bank slot carries no type/text,
   * so without this the preview has nothing renderable to show.
   */
  previewDraw(quizId: string): Observable<QuizAttemptQuestion[]> {
    return this.http
      .post<{ success: boolean; data: { preview: boolean; questions: QuizAttemptQuestion[] } }>(
        `${this.apiUrl}/${quizId}/attempts`,
        { preview: true }
      )
      .pipe(map(r => r?.data?.questions ?? []));
  }

  /**
   * KAN-348 Δ3: Autosave in-progress answers onto the open attempt (display space).
   */
  saveAttemptAnswers(quizId: string, attemptId: string, answers: any[]): Observable<{ savedAt?: string }> {
    return this.http
      .put<{ success: boolean; data: { savedAt?: string } }>(
        `${this.apiUrl}/${quizId}/attempts/${attemptId}/answers`,
        { answers }
      )
      .pipe(map(r => r?.data ?? {}));
  }

  // CB-060: Partial Quiz Scoring Methods
  // CRITICAL FIX: Backend returns passingScore from quiz data, not hardcoded default
  // KAN-348: submissions carry the open attemptId so grading runs against the
  // frozen attempt snapshot (bank draws / shuffled order).
  submitQuiz(quizId: string, answers: any[], opts: SubmitOptions = {}): Observable<any> {
    return this.http.post(`${this.apiUrl}/${quizId}/submit`, {
      answers,
      attemptId: opts.attemptId,
      moduleId: opts.moduleId,
      unitId: opts.unitId,
      courseId: opts.courseId,
      timeSpent: opts.timeSpent,
      videoProgress: opts.videoProgress
    });
  }

  getQuizResults(quizId: string, userId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/${quizId}/results/${userId}`);
  }

  /**
   * Fetch a quiz by its ID — live source of truth.
   * Used by the learner course player to refetch quiz questions on
   * soft-navigation so instructor edits propagate without a hard refresh.
   * KAN-191 Bug 1.
   */
  getQuizById(quizId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/${quizId}`);
  }

  /**
   * Fetch a module's quiz by module ID — live source of truth.
   * Used by the learner course player to refetch module quiz questions on
   * soft-navigation so instructor edits propagate without a hard refresh.
   * KAN-191 Bug 1.
   */
  getQuizByModuleId(moduleId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/module/${moduleId}`);
  }

  /**
   * Get latest quiz submission for current user
   * @implements REQ-GRADE-203 - View quiz submission answers
   * Returns null when no submission exists (backend returns {found: false})
   */
  getDebugAnswers(quizId: string): Observable<DebugQuizAnswers> {
    return this.http.get<DebugQuizAnswers>(`${environment.apiUrl}/debug/quizzes/${quizId}/answers`);
  }

  getLatestSubmission(
    quizId: string,
    userId?: string,
    courseId?: string,
    // KAN-270: scope to the specific placement of a reused quiz so a sibling
    // placement never returns this submission. moduleId for a module placement,
    // unitId for an end-of-unit placement.
    placement?: { moduleId?: string; unitId?: string }
  ): Observable<QuizSubmission | null> {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (courseId) params.set('courseId', courseId);
    if (placement?.moduleId) params.set('moduleId', placement.moduleId);
    if (placement?.unitId) params.set('unitId', placement.unitId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.http.get<{success: boolean, data: QuizSubmission | {found: boolean, quizCompleted: boolean}, timestamp: string}>(
      `${this.apiUrl}/${quizId}/submissions/latest${queryString}`
    ).pipe(
      map(response => {
        // Backend returns {found: false} when no submission exists
        // Return null for consistent handling in components
        if (response.data && typeof response.data === 'object' && 'found' in response.data && !response.data.found) {
          return null;
        }
        return response.data as QuizSubmission;
      })
    );
  }
} 